<script setup lang="ts">
import { computed, ref } from 'vue'

import { ButtonComponent, CountBadgeComponent, FlexComponent, NestedOptionRow, Selector, SelectorOptionRow } from '../../'
import type { ListItems } from '../../../types/common.types'
import type { ContentTypeOption } from '../../../types/content-item-platform.types'

const props = withDefaults(
  defineProps<{
    contentTypeOptions: ContentTypeOption[]
    shouldTruncateText?: boolean
    displayMode?: 'vertical' | 'horizontal'
    label?: string
  }>(),
  {
    displayMode: 'vertical',
    label: 'Source',
  },
)

const emits = defineEmits(['update:contentTypes'])
const contentTypes = defineModel<string[] | undefined>('contentTypes')

const indexedOptions = computed(() => props.contentTypeOptions.map((item, index) => ({ ...item, id: index })))

type IndexedOption = (typeof indexedOptions.value)[number]

const selectedTypes = computed(() => contentTypes.value ?? [])

const contentTypesOf = (option: ContentTypeOption | undefined): string[] => (option?.contentTypes ?? []).map((type) => type.value)

// There is no `source` model: the picked source is whichever one owns the selected types.
const selectedOption = computed(
  () => indexedOptions.value.find((item) => contentTypesOf(item).some((type) => selectedTypes.value.includes(type))) ?? null,
)

const hasSubOptions = computed(() => props.contentTypeOptions.some((option) => option.narrowable))

const optionOf = (id: number): IndexedOption | undefined => indexedOptions.value.find((option) => option.id === id)

const valueOf = (id: number) => optionOf(id)?.value

const subOptionsOf = (id: number): ListItems | undefined => {
  const option = optionOf(id)
  return option?.narrowable ? option.contentTypes : undefined
}

const selectedLabel = computed(() => selectedOption.value?.label ?? props.label)

const openSubMenu = ref<string | null>(null)

const isSubMenuOpen = (id: number) => openSubMenu.value === valueOf(id)

// Idempotent, not a toggle: hover already opened the submenu, so a toggle would close it on the
// click that follows, with no fresh `mouseenter` to reopen it.
const openSubMenuFor = (id: number) => {
  openSubMenu.value = valueOf(id) ?? null
}

const handleParentClick = (id: number) => {
  if (valueOf(id) === selectedOption.value?.value && isSubMenuOpen(id)) {
    emits('update:contentTypes', undefined)
    return
  }

  openSubMenuFor(id)
}

const handleVisibleChange = (visible: boolean) => {
  if (!visible) {
    openSubMenu.value = null
  }
}

const emitForSource = (option: ContentTypeOption | undefined) => {
  const expanded = contentTypesOf(option)
  emits('update:contentTypes', expanded.length ? expanded : undefined)
}

const handleSourceSelection = (item: { id: number; label?: string } | null) => {
  openSubMenu.value = null
  emitForSource(item ? optionOf(item.id) : undefined)
}

const checkedTypesOf = (id: number): string[] => {
  const available = subOptionsOf(id)
  if (!available) {
    return []
  }

  return available.map((option) => option.value).filter((value) => selectedTypes.value.includes(value))
}

const selectedTypesCount = computed(() => (selectedOption.value ? checkedTypesOf(selectedOption.value.id).length : 0))

const handleTypeToggle = (id: number, typeValue: string) => {
  const available = subOptionsOf(id)
  if (!available) {
    return
  }
  const current = checkedTypesOf(id)
  const next = new Set(current.includes(typeValue) ? current.filter((item) => item !== typeValue) : [...current, typeValue])
  const ordered = available.filter((option) => next.has(option.value)).map((option) => option.value)

  emits('update:contentTypes', ordered.length ? ordered : undefined)
}

const handleButtonClick = (item: IndexedOption) => {
  emitForSource(selectedOption.value?.value === item.value ? undefined : item)
}
</script>

<template>
  <Selector
    v-if="displayMode === 'vertical'"
    :options="indexedOptions"
    :selected-option="selectedOption"
    :selected-label="selectedLabel"
    :should-truncate-text="shouldTruncateText"
    :show-search="false"
    :menu-overflow="hasSubOptions ? 'visible' : 'auto'"
    @update:selected-option="handleSourceSelection"
    @visible-change="handleVisibleChange"
  >
    <template v-if="hasSubOptions" #labelSuffix>
      <CountBadgeComponent :count="selectedTypesCount" />
    </template>
    <template v-if="hasSubOptions" #option="{ item, selected }">
      <NestedOptionRow
        v-if="subOptionsOf(item.id)"
        :label="item.label"
        :options="subOptionsOf(item.id) ?? []"
        :checked-values="checkedTypesOf(item.id)"
        :open="isSubMenuOpen(item.id)"
        :selected="selected"
        @activate="handleParentClick(item.id)"
        @open="openSubMenuFor(item.id)"
        @close="openSubMenu = null"
        @toggle="handleTypeToggle(item.id, $event)"
      />
      <SelectorOptionRow v-else :label="item.label" :selected="selected" :truncate="shouldTruncateText" />
    </template>
  </Selector>
  <FlexComponent v-else :gap="8" wrap="wrap">
    <ButtonComponent
      v-for="option in indexedOptions"
      :key="option.id"
      variant="text"
      class="source-selector-button"
      :class="{ 'is-active': selectedOption?.value === option.value }"
      @click="handleButtonClick(option)"
    >
      {{ option.label }}
    </ButtonComponent>
  </FlexComponent>
</template>
<style lang="scss" scoped>
.button.source-selector-button {
  padding: 8px 16px;
  &.is-active {
    background-color: var(--button-hover);
  }
}
</style>
